'use client';

/**
 * TestimonialVideo — a video testimonial in OUR player, with the name over the
 * frame (Senja sunset, 2026-09-26). Used for "A real student, on video" on
 * /super-fingers and the Iolo card in the /store review strip; replaces Senja's
 * single_video widget 42be0410.
 *
 * Player: components/video/TestimonialPlayer (the website's testimonial
 * player, the same one the Wall of Love uses — never the locked lesson player,
 * SOP-PLATFORM-04), fed from our Bunny Testimonials library (761111), with the
 * feed's captions and trim honoured. Play button: the kit's §14.2
 * `.pf-play-btn`. Overlay: Aria §4 — gold 4px bar above the name (Cormorant
 * 600 20px, --text) on a dark bottom gradient; it goes once the video starts.
 *
 * Lazy like WolVideo: until the frame is within 400px of the viewport only the
 * poster image is there. Inside the review strip, the strip holds still while
 * the video plays.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { TestimonialPlayer } from '@/components/video/TestimonialPlayer';
import PfPlayButton from '@/components/video/PfPlayButton';
import type { WolCaption } from '@/lib/wall-of-love-feed';
import { useMarqueeHold } from './ReviewMarquee';
import { ReviewStars } from './ReviewModal';
import './reviews.css';

import { TESTIMONIALS_CDN_HOSTNAME, testimonialPosterSrc } from './cdn';
export { TESTIMONIALS_CDN_HOSTNAME };

export default function TestimonialVideo({
  guid,
  posterUrl = null,
  captions,
  trimIn,
  trimOut,
  title,
  name,
  rating,
  playLabel,
}: {
  guid: string;
  /** Our media-host copy of the poster (feed video.posterUrl); null = pull-zone thumbnail. */
  posterUrl?: string | null;
  captions: WolCaption[];
  trimIn: number | null;
  trimOut: number | null;
  title: string;
  name: string;
  rating?: number | null;
  playLabel: string;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const hold = useMarqueeHold();
  const key = useId();
  const poster = testimonialPosterSrc({ guid, posterUrl });

  useEffect(() => {
    const el = boxRef.current;
    if (!el || near) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: '400px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near]);

  // While it plays, a surrounding review strip holds still.
  useEffect(() => {
    if (!video) return;
    const on = () => hold(key, true);
    const off = () => hold(key, false);
    video.addEventListener('play', on);
    video.addEventListener('pause', off);
    video.addEventListener('ended', off);
    return () => {
      video.removeEventListener('play', on);
      video.removeEventListener('pause', off);
      video.removeEventListener('ended', off);
      off();
    };
  }, [video, hold, key]);

  const overlay = (
    <div className="rv-video__overlay" aria-hidden="true">
      {rating ? <ReviewStars rating={rating} /> : null}
      <div className="rv-video__who">
        <span className="rv-video__bar" />
        <p className="rv-video__name">{name}</p>
      </div>
    </div>
  );

  return (
    <div className="rv-video" ref={boxRef} data-testid="testimonial-video">
      <div className="rv-video__stage">
        {near ? (
          <TestimonialPlayer
            source={{ kind: 'bunny', videoId: guid, cdnHostname: TESTIMONIALS_CDN_HOSTNAME }}
            title={title}
            poster={poster}
            tracks={captions}
            clip={{ trimIn, trimOut }}
            maxHeight="calc(100cqw * 3 / 4)"
            playLabel={playLabel}
            overlay={overlay}
            bigPlay="kit"
            onVideoElement={setVideo}
          />
        ) : (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img className="rv-video__poster" src={poster} alt="" loading="lazy" decoding="async" draggable={false} />
            {overlay}
            <PfPlayButton label={playLabel} onClick={() => setNear(true)} />
          </>
        )}
      </div>
    </div>
  );
}
